/**
 * Korzo chart helpers for prescribing workout target splits.
 * Keep in sync with backend/utils/korzoChart.js.
 */

const RATE_SPEED_PERCENT = [
  [16, 75.0],
  [18, 77.5],
  [20, 80.0],
  [22, 82.5],
  [24, 85.0],
  [26, 87.5],
  [28, 90.0],
  [30, 92.0],
  [32, 94.0],
  [34, 96.0],
  [36, 98.0],
  [38, 100.0]
];

const GOAL_BOAT_CLASS_ALIASES = {
  '2x/-': '2x',
  '4x/-': '4x',
  '4+/x': '4+'
};

const toNum = (value) => {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

const round1 = (value) => Math.round(value * 10) / 10;
const round2 = (value) => Math.round(value * 100) / 100;

export const GOAL_BOAT_CLASSES = ['Erg', '1x', '2x', '2-', '4x', '4-', '4+', '8+'];

export function goalBoatClassKey(boatClass) {
  if (!boatClass) return null;
  const normalized = String(boatClass)
    .replace(/^Men's\s*/i, 'M ')
    .replace(/^Women's\s*/i, 'W ')
    .replace(/\s+/g, ' ')
    .trim();
  const stripped = normalized.replace(/^[MW]\s+/i, '').trim();
  if (!stripped) return null;
  if (/^erg$/i.test(stripped)) return 'Erg';
  return GOAL_BOAT_CLASS_ALIASES[stripped] || stripped;
}

export function speedPercentForRate(strokeRate) {
  const rate = toNum(strokeRate);
  if (rate == null || rate <= 0) return null;
  if (rate <= RATE_SPEED_PERCENT[0][0]) return RATE_SPEED_PERCENT[0][1];
  const last = RATE_SPEED_PERCENT[RATE_SPEED_PERCENT.length - 1];
  if (rate >= last[0]) return last[1];
  for (let i = 0; i < RATE_SPEED_PERCENT.length - 1; i += 1) {
    const [r0, p0] = RATE_SPEED_PERCENT[i];
    const [r1, p1] = RATE_SPEED_PERCENT[i + 1];
    if (rate >= r0 && rate <= r1) {
      const t = (rate - r0) / (r1 - r0);
      return round2(p0 + t * (p1 - p0));
    }
  }
  return null;
}

export function suggestedRateForPercent(percent) {
  const p = toNum(percent);
  if (p == null) return null;
  if (p <= RATE_SPEED_PERCENT[0][1]) return RATE_SPEED_PERCENT[0][0];
  const last = RATE_SPEED_PERCENT[RATE_SPEED_PERCENT.length - 1];
  if (p >= last[1]) return last[0];
  for (let i = 0; i < RATE_SPEED_PERCENT.length - 1; i += 1) {
    const [r0, p0] = RATE_SPEED_PERCENT[i];
    const [r1, p1] = RATE_SPEED_PERCENT[i + 1];
    if (p >= p0 && p <= p1) {
      const t = (p - p0) / (p1 - p0);
      return round1(r0 + t * (r1 - r0));
    }
  }
  return null;
}

export function targetSplitForPercent(twoKSeconds, percent) {
  const twoK = toNum(twoKSeconds);
  const p = toNum(percent);
  if (twoK == null || twoK <= 0 || p == null || p <= 0) return null;
  return (twoK / 4) / (p / 100);
}

export function percentOfTwoKSpeed(actualSplitSeconds, twoKSeconds) {
  const split = toNum(actualSplitSeconds);
  const twoK = toNum(twoKSeconds);
  if (split == null || split <= 0 || twoK == null || twoK <= 0) return null;
  return round1(((twoK / 4) / split) * 100);
}

export function workoutTarget({ twoKSeconds, goalPercent, strokeRate } = {}) {
  const goal2k = toNum(twoKSeconds);
  if (goal2k == null || goal2k <= 0) return null;
  const rate = toNum(strokeRate);
  const percent = rate != null ? speedPercentForRate(rate) : toNum(goalPercent);
  if (percent == null || percent <= 0) return null;
  const split = targetSplitForPercent(goal2k, percent);
  if (split == null) return null;
  return {
    goal_2k_seconds: round2(goal2k),
    goal_percent: round2(percent),
    suggested_rate: rate != null ? round1(rate) : suggestedRateForPercent(percent),
    target_split_seconds: round2(split)
  };
}

const effectiveGoal2kSeconds = (athlete = {}) => {
  const coach = toNum(athlete.coach_goal_2k_seconds);
  const own = toNum(athlete.goal_2k_seconds);
  if (coach != null && coach > 0) return coach;
  if (own != null && own > 0) return own;
  const effective = toNum(athlete.effective_goal_2k_seconds);
  if (effective != null && effective > 0) return effective;
  return null;
};

export { effectiveGoal2kSeconds };

export function athleteGoalRows(athlete) {
  const rows = [];
  const seen = new Set();
  const add = (boatClass, seconds) => {
    const time = toNum(seconds);
    if (!boatClass || time == null || time <= 0 || seen.has(boatClass)) return;
    seen.add(boatClass);
    rows.push({ boat_class: boatClass, two_k_seconds: time });
  };
  add('Default', effectiveGoal2kSeconds(athlete));
  (athlete?.two_k_goals || []).forEach((row) => {
    add(row.boat_class, effectiveGoal2kSeconds(row));
  });
  return rows;
}

export const RATE_PRESETS = [
  { rate: 38, label: 'Race / r38' },
  { rate: 28, label: 'r28' },
  { rate: 26, label: 'r26' },
  { rate: 24, label: 'AT / r24' },
  { rate: 20, label: 'SS / r20' },
  { rate: 16, label: 'r16' }
];

export const GOAL_PERCENT_PRESETS = RATE_PRESETS.map((preset) => ({
  value: RATE_SPEED_PERCENT.find(([rate]) => rate === preset.rate)?.[1],
  label: preset.label,
  rate: preset.rate
}));

export function pickTarget(targets = [], boatClass) {
  if (!targets.length) return null;
  const key = goalBoatClassKey(boatClass);
  if (key) {
    const match = targets.find((row) => row.boat_class === key);
    if (match) return match;
  }
  return targets.find((row) => row.boat_class === 'Default') || null;
}

export function athleteWorkoutTarget(athlete, boatClass, goalPercent) {
  if (!athlete) return null;
  const classGoals = {};
  (athlete.two_k_goals || []).forEach((row) => {
    if (row.boat_class) classGoals[row.boat_class] = row;
  });
  const key = goalBoatClassKey(boatClass);
  const classRow = key ? classGoals[key] : null;
  const twoK = (classRow && effectiveGoal2kSeconds(classRow)) || effectiveGoal2kSeconds(athlete);
  const target = workoutTarget({ twoKSeconds: twoK, goalPercent });
  if (!target) return null;
  return { boat_class: key || 'Default', ...target };
}
